import { erpToast } from 'design-system/components/erp-toast'
import { attentionTotalFromPendingActions, type ManualOfflineSyncOutcome } from './offlineBootstrap'

function formatAttentionBreakdown(pa: ManualOfflineSyncOutcome['bootstrap']['pending_actions']): string {
  const parts: string[] = []
  const pending = pa.pending_mutations + pa.pending_pos_orders + pa.pending_table_ops
  if (pending > 0) {
    const detail: string[] = []
    if (pa.pending_pos_orders > 0) detail.push(`${pa.pending_pos_orders} نقطة بيع`)
    if (pa.pending_table_ops > 0) detail.push(`${pa.pending_table_ops} طاولات`)
    if (pa.pending_mutations > 0) detail.push(`${pa.pending_mutations} عمليات أخرى`)
    parts.push(`قيد: ${detail.join('، ')}`)
  }
  const failed = pa.failed_orders + pa.failed_table_ops + pa.failed_mutations
  if (failed > 0) {
    const detail: string[] = []
    if (pa.failed_orders > 0) detail.push(`${pa.failed_orders} مبيعات`)
    if (pa.failed_table_ops > 0) detail.push(`${pa.failed_table_ops} طاولات`)
    if (pa.failed_mutations > 0) detail.push(`${pa.failed_mutations} عمليات أخرى`)
    parts.push(`فشل: ${detail.join('، ')}`)
  }
  const conflicts = pa.mutation_conflicts + pa.pos_order_conflicts + pa.table_op_conflicts
  if (conflicts > 0) {
    parts.push(`تعارض: ${conflicts}`)
  }
  return parts.join(' · ')
}

/** User-visible result for «مزامنة الآن» — honest about partial failure and dead-letter orders. */
export function notifyManualSyncOutcome(outcome: ManualOfflineSyncOutcome): void {
  if (outcome.skippedOffline) {
    erpToast.warning(outcome.bootstrap.message || 'لا يوجد اتصال بالإنترنت حاليًا')
    return
  }

  const pa = outcome.bootstrap.pending_actions
  const attention = attentionTotalFromPendingActions(pa)

  const replayRowErrors = outcome.replay.errors.length
  const replayIssue = replayRowErrors > 0 || outcome.replayThrewMessage
  const pushErrors = outcome.posPush?.errors ?? []
  const pushRowErrors = pushErrors.length
  const pushException = outcome.posPushThrewMessage
  const pushIssue = pushRowErrors > 0 || Boolean(pushException)

  if (pa.failed_orders > 0) {
    const extra =
      pushRowErrors > 0 && pa.failed_orders > 0
        ? ` — ${pushErrors[0]}`
        : ''
    erpToast.warning(
      `تمت المزامنة، لكن توجد ${pa.failed_orders} طلب(ات) فشلت وتحتاج مراجعة من قائمة «فشل مبيعات POS»${extra}`,
      { duration: 7000 },
    )
    return
  }

  if (pa.failed_table_ops > 0) {
    erpToast.warning(
      `تمت المزامنة، لكن توجد ${pa.failed_table_ops} عملية طاولة فاشلة — راجع قائمة «فشل طلبات الطاولات».`,
      { duration: 7000 },
    )
    return
  }

  if (pa.failed_mutations > 0) {
    erpToast.warning(
      `تمت المزامنة، لكن توجد ${pa.failed_mutations} عملية أخرى فاشلة — راجع قائمة «عمليات أخرى فاشلة».`,
      { duration: 7000 },
    )
    return
  }

  const conflictTotal = pa.mutation_conflicts + pa.pos_order_conflicts + pa.table_op_conflicts
  if (conflictTotal > 0) {
    erpToast.warning(
      `توجد تعارضات (${conflictTotal}) تحتاج مراجعة قبل إتمام المزامنة.`,
      { duration: 6500 },
    )
    return
  }

  if (replayIssue || pushIssue) {
    const parts: string[] = []
    if (outcome.replayThrewMessage) parts.push(outcome.replayThrewMessage)
    parts.push(...outcome.replay.errors.slice(0, 2))
    if (pushException) parts.push(pushException)
    parts.push(...pushErrors.slice(0, 2))
    const compact = parts.filter(Boolean).slice(0, 3).join(' · ')
    erpToast.warning(
      compact ? `تمت المزامنة جزئياً: ${compact}` : 'تمت المزامنة جزئياً — راجع التفاصيل',
      { duration: 7000 },
    )
    return
  }

  if (attention > 0) {
    const breakdown = formatAttentionBreakdown(pa)
    erpToast.warning(
      breakdown
        ? `ما زال هناك ${attention} عنصراً يحتاج مزامنة (${breakdown}). راجع قائمة المزامنة في الهيدر.`
        : `ما زال هناك ${attention} عنصراً يحتاج مزامنة (قيد / تعارض / فشل). راجع قائمة المزامنة في الهيدر.`,
      { duration: 7000 },
    )
    return
  }

  if (outcome.bootstrap.status === 'partial_failed' || outcome.bootstrap.status === 'failed') {
    erpToast.warning(
      outcome.bootstrap.message || 'اكتملت المزامنة مع أخطاء في تجهيز بعض البيانات.',
      { duration: 6500 },
    )
    return
  }

  erpToast.success('تمت المزامنة بنجاح')
}
